/**
 * Pure, framework-agnostic core for container-width breakpoints, modelled as a **breakpoint size scale**
 * (Tailwind-style). No React, no DOM — given a measured width and the per-size min-widths it answers
 * two questions: "which size is the container right now?" and "which variant should render?". Kept
 * separate so it is trivially unit-testable.
 *
 * The model is **min-width / mobile-first**, exactly like Tailwind: a size engages when the container is
 * *at least* its min-width, and the current size is the widest such size. A `from` stage renders from
 * its size upward. This is the inverse of a max-width / "collapse below" model — `from: "lg"` reads as
 * "lg and wider", matching the muscle memory Tailwind builds.
 */

/** Canonical breakpoint scale, widest → narrowest. Defines the ordering every comparison keys off. */
export const BREAKPOINT_SCALE = ["2xl", "xl", "lg", "md", "sm", "xs", "2xs"] as const;

/** One step on {@link BREAKPOINT_SCALE}. */
export type BreakpointSize = (typeof BREAKPOINT_SCALE)[number];

/**
 * Per-size min container width (px). Declare only the sizes you need; list them with descending widths.
 * The smallest declared size is the floor — give it `0` so it matches at any width.
 */
export type BreakpointTiers = Partial<Record<BreakpointSize, number>>;

/** Position on the scale; a lower rank is wider. Every size comparison keys off this. */
export function rank(size: BreakpointSize): number {
	return BREAKPOINT_SCALE.indexOf(size);
}

/** `true` when `size` is `floor` or wider (`size` ≥ `floor` on the scale). */
export function atLeast(size: BreakpointSize, floor: BreakpointSize): boolean {
	return rank(size) <= rank(floor);
}

/** `true` when `size` is `ceil` or narrower (`size` ≤ `ceil` on the scale). */
export function atMost(size: BreakpointSize, ceil: BreakpointSize): boolean {
	return rank(size) >= rank(ceil);
}

/** One declared size paired with its min container width. A list of these is sorted widest → narrowest. */
export type SizeTier = { readonly size: BreakpointSize; readonly minWidth: number };

/**
 * Normalize a {@link BreakpointTiers} record into a width-ordered (widest → narrowest) list, validating that
 * the min-widths strictly descend along the scale. Throws on an empty or misordered config — caught at
 * module load, since the engine is created once. Returns at least one tier.
 */
export function toSizeTiers(tiers: BreakpointTiers): SizeTier[] {
	const ordered: SizeTier[] = BREAKPOINT_SCALE.filter((size) => tiers[size] !== undefined).map((size) => ({
		size,
		minWidth: tiers[size] as number,
	}));
	if (ordered.length === 0) {
		throw new Error("container-breakpoints: declare at least one breakpoint size.");
	}
	for (let i = 1; i < ordered.length; i++) {
		if (ordered[i].minWidth >= ordered[i - 1].minWidth) {
			throw new Error(
				`container-breakpoints: min-widths must strictly descend along the scale; "${ordered[i].size}" ` +
					`(${ordered[i].minWidth}) is not below "${ordered[i - 1].size}" (${ordered[i - 1].minWidth}).`,
			);
		}
	}
	return ordered;
}

/**
 * The current size for a measured width: the widest declared tier whose min-width the container meets.
 * An `undefined` width (not yet measured) is treated as infinitely wide → the widest tier, so the UI
 * starts fully expanded and the first resize narrows it. `tiers` must be pre-ordered by {@link toSizeTiers}.
 */
export function computeSize(width: number | undefined, tiers: readonly SizeTier[]): BreakpointSize {
	const w = width ?? Number.POSITIVE_INFINITY;
	for (const tier of tiers) {
		if (w >= tier.minWidth) {
			return tier.size;
		}
	}
	return tiers[tiers.length - 1].size;
}

/**
 * Pick the matching variant for the current size from an ordered (widest → narrowest) list. Each stage
 * except the last carries a `from` size: it renders when the current size is `from` or wider. Returns
 * the first eligible stage, falling back to the last (narrowest) stage. Pure.
 */
export function pickStage<T extends { from?: BreakpointSize }>(
	stages: readonly T[],
	size: BreakpointSize,
): T | undefined {
	for (const stage of stages) {
		if (stage.from === undefined || atLeast(size, stage.from)) {
			return stage;
		}
	}
	return stages[stages.length - 1];
}
